import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Step1OngoingRoles from './Step1OngoingRoles';
import Step2CurrentProjects from './Step2CurrentProjects';
import Step3PastProjects from './Step3PastProjects';
import Step3FutureProjects from './Step3FutureProjects';
import Step4Review from './Step4Review';
import Step5Success from './Step5Success';
import OnboardingLayout from './OnboardingLayout';
import { removeOngoingRole } from '../../../store/slices/onboardingSlice';


/**
 * OnboardingFlow
 * ------------------------------------------------------------------
 * OPTIONAL reference wiring - shows how the 6 steps share state and
 * move forward/back. Drop this inside your own layout/route, or
 * just copy the state-lifting pattern into your existing screen
 * controller and delete this file.
 */
const STEP_SLUGS = {
  1: 'ongoing-roles',
  2: 'past-projects',
  3: 'current-projects',
  4: 'future-projects',
  5: 'review',
  6: 'success',
};

const SLUG_TO_STEP = Object.fromEntries(
  Object.entries(STEP_SLUGS).map(([n, slug]) => [slug, parseInt(n, 10)])
);

const OnboardingFlow = ({ onComplete }) => {
  const { step } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.onboarding?.ongoingRoles || []);
  const [currentProjects, setCurrentProjects] = useState([]);
  const [pastProjects, setPastProjects] = useState([]);
  const [futureProjects, setFutureProjects] = useState([]);

  const editId = searchParams.get('edit');
  const fromReview = searchParams.get('from') === 'review';

  const currentStep = Math.min(6, Math.max(1, SLUG_TO_STEP[step] || 1));
  const goTo = (n) =>
    navigate(`/onboarding/${STEP_SLUGS[Math.min(6, Math.max(1, n))]}`);

  const removeAt = (setter) => (index) => setter((prev) => prev.filter((_, i) => i !== index));

  switch (currentStep) {
    case 1:
      return (
        <OnboardingLayout>
          <Step1OngoingRoles
            editId={editId}
            fromReview={fromReview}
            onNext={() => {
              if (fromReview) goTo(5);
              else goTo(2);
            }}
          />
        </OnboardingLayout>
      );

    case 2:
      return (
        <OnboardingLayout>
          <Step3PastProjects
            editId={editId}
            fromReview={fromReview}
            onBack={() => goTo(1)}
            onNext={(past) => {
              setPastProjects(past);
              if (fromReview) goTo(5);
              else goTo(3);
            }}
          />
        </OnboardingLayout>
      );

    case 3:
      return (
        <OnboardingLayout>
          <Step2CurrentProjects
            editId={editId}
            fromReview={fromReview}
            onBack={() => goTo(2)}
            onNext={(current) => {
              setCurrentProjects(current);
              if (fromReview) goTo(5);
              else goTo(4);
            }}
          />
        </OnboardingLayout>
      );

    case 4:
      return (
        <OnboardingLayout>
          <Step3FutureProjects
            editId={editId}
            fromReview={fromReview}
            onBack={() => goTo(3)}
            onNext={(future) => {
              setFutureProjects(future);
              if (fromReview) goTo(5);
              else goTo(5);
            }}
          />
        </OnboardingLayout>
      );

    case 5:
      return (
        <OnboardingLayout>
          <Step4Review
            roles={roles}
            currentProjects={currentProjects}
            pastProjects={pastProjects}
            futureProjects={futureProjects}
            onDeleteRole={(index) => dispatch(removeOngoingRole(index))}
            onDeleteCurrent={removeAt(setCurrentProjects)}
            onDeletePast={removeAt(setPastProjects)}
            onDeleteFuture={removeAt(setFutureProjects)}
            onNext={() => {
              onComplete?.({ roles, currentProjects, pastProjects, futureProjects });
              navigate("/dashboard", { replace: true })
            }}
          />
        </OnboardingLayout>
      );

    case 6:
    default:
      return (
        <OnboardingLayout>
          <Step5Success
            onBack={() => goTo(5)}
            onComplete={() =>
              onComplete?.({ roles, currentProjects, pastProjects, futureProjects })
            }
          />
        </OnboardingLayout>
      );
  }
};

export default OnboardingFlow;
